/**
 * UserMenu - A molecule component for displaying authenticated user menu.
 *
 * Clickable user button that expands to show user info and logout option.
 * Used in navigation bars when user is authenticated.
 */

import { useState } from 'react';
import { User, LogOut, Users, Shield, Settings, Wifi, WifiOff, Database, Plug } from 'lucide-react';
import { cn } from '@lean-agility/design-system/react';
import type { AuthUser } from '../../types';
import { Dropdown } from '@/components/molecules/Dropdown';
import { Menu } from '@/components/molecules/Menu';
import { MenuItem } from '@/components/atoms/MenuItem';
import { MenuButton } from '@/components/atoms/MenuButton';
import { UserInfo } from './UserInfo';

export type SyncStatus = 'live' | 'polling' | 'connecting' | null;

export interface UserMenuProps {
  /**
   * The authenticated user.
   */
  user: AuthUser;
  /**
   * Callback invoked when logout button is clicked.
   */
  onLogout: () => void;
  /**
   * Callback invoked when "Manage users" menu item is clicked.
   * Only shown for admin users.
   */
  onManageUsersClicked?: () => void;
  /**
   * Callback invoked when "License" menu item is clicked.
   * Only shown for admin users.
   */
  onLicenseClicked?: () => void;
  /**
   * Callback invoked when "Tracker sources" menu item is clicked.
   * Only shown for admin users.
   */
  onTrackerSourcesClicked?: () => void;
  /**
   * Callback invoked when "Datasets" menu item is clicked.
   */
  onDatasetsClicked?: () => void;
  /**
   * Whether the tracker feature is licensed for this deployment. When false,
   * the "Tracker sources" and "Datasets" entries are hidden (the feature
   * isn't part of the plan). Configuration state is handled on the pages.
   */
  showTracker?: boolean;
  /**
   * Callback invoked when "Account settings" menu item is clicked.
   */
  onAccountSettingsClicked?: () => void;
  /**
   * Current sync/connection status. Only shown when a simulation is selected.
   */
  syncStatus?: SyncStatus;
  /**
   * Callback when sync status is clicked (to toggle between modes).
   */
  onSyncClick?: () => void;
  /**
   * Additional CSS classes.
   */
  className?: string;
}

export function UserMenu({
  user,
  onLogout,
  onManageUsersClicked,
  onLicenseClicked,
  onTrackerSourcesClicked,
  onDatasetsClicked,
  showTracker = false,
  onAccountSettingsClicked,
  syncStatus,
  onSyncClick,
  className,
}: UserMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  // Use name if available, otherwise derive from email (part before @)
  const displayName = user.name || user.email.split('@')[0] || user.email;

  const handleLogout = () => {
    setIsOpen(false);
    onLogout();
  };

  const handleManageUsers = () => {
    setIsOpen(false);
    onManageUsersClicked?.();
  };

  const handleLicense = () => {
    setIsOpen(false);
    onLicenseClicked?.();
  };

  const handleTrackerSources = () => {
    setIsOpen(false);
    onTrackerSourcesClicked?.();
  };

  const handleDatasets = () => {
    setIsOpen(false);
    onDatasetsClicked?.();
  };

  const handleAccountSettings = () => {
    setIsOpen(false);
    onAccountSettingsClicked?.();
  };

  const handleSyncClick = () => {
    onSyncClick?.();
  };

  // Sync status display configuration
  const syncConfig = {
    live: {
      label: 'Live',
      icon: Wifi,
      colorClass: 'text-feedback-info',
      title: 'Real-time sync active. Click to switch to polling.',
    },
    polling: {
      label: 'Polling',
      icon: Wifi,
      colorClass: 'text-amber-500',
      title: 'Polling for updates. Click to try real-time sync.',
    },
    connecting: {
      label: 'Connecting...',
      icon: WifiOff,
      colorClass: 'text-text-muted',
      title: 'Attempting to connect. Click to retry.',
    },
  };

  return (
    <Dropdown
      controlled
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      className={cn(className)}
      trigger={
        <MenuButton icon={User} isOpen={isOpen} aria-label="User menu">
          {displayName}
        </MenuButton>
      }
    >
      <Menu width="xl">
        <UserInfo user={user} />
        {syncStatus && (
          <div className="border-b border-border-default/50 px-3 py-2">
            <button
              onClick={handleSyncClick}
              className="
                flex w-full items-center justify-between text-sm transition-opacity
                hover:opacity-80
              "
              title={syncConfig[syncStatus].title}
            >
              <span className="text-text-secondary">Sync</span>
              <span className={cn('flex items-center gap-1.5', syncConfig[syncStatus].colorClass)}>
                {(() => {
                  const IconComponent = syncConfig[syncStatus].icon;
                  return <IconComponent className="size-3.5" />;
                })()}
                {syncConfig[syncStatus].label}
              </span>
            </button>
          </div>
        )}
        <div className="p-1">
          {user.is_admin && (
            <>
              <MenuItem icon={Users} onClick={handleManageUsers}>
                Manage users
              </MenuItem>
              <MenuItem icon={Shield} onClick={handleLicense}>
                License
              </MenuItem>
              {showTracker && (
                <MenuItem icon={Plug} onClick={handleTrackerSources}>
                  Tracker sources
                </MenuItem>
              )}
            </>
          )}
          {showTracker && (
            <MenuItem icon={Database} onClick={handleDatasets}>
              Datasets
            </MenuItem>
          )}
          <MenuItem icon={Settings} onClick={handleAccountSettings}>
            Account settings
          </MenuItem>
          <MenuItem icon={LogOut} onClick={handleLogout}>
            Logout
          </MenuItem>
        </div>
      </Menu>
    </Dropdown>
  );
}
